import { useEffect, useState } from "react";
import { X, AlertTriangle, PauseCircle, Trash2 } from "lucide-react";

const REASONS = [
  { key: "got_a_job",          label: "I got a job 🎉",                  celebration: true },
  { key: "not_finding_matches", label: "Not finding good matches" },
  { key: "too_expensive",       label: "Too expensive" },
  { key: "not_using_enough",    label: "Not using it enough" },
  { key: "taking_a_break",      label: "Taking a break from the search" },
  { key: "privacy_concerns",    label: "Privacy concerns" },
  { key: "try_different_tool",  label: "Trying a different tool" },
  { key: "other",               label: "Something else" },
];

/**
 * Two-stage modal: pick a reason, optionally leave a note, then confirm.
 * For delete: also requires a typed-DELETE confirmation.
 * Calls onSubmit({ reason, note, celebrate }) - celebrate is true when the
 * user picked "I got a job" so the parent can render the celebration overlay
 * BEFORE the underlying API request runs.
 */
export default function AccountActionModal({ mode, open, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open) {
      setReason(null);
      setNote("");
      setConfirmText("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const isDelete = mode === "delete";
  const Icon = isDelete ? Trash2 : PauseCircle;
  const accent = isDelete ? "text-destructive" : "text-accent-amber";
  const accentBorder = isDelete ? "border-destructive/40" : "border-accent-amber/40";

  const deleteReady = !isDelete || confirmText.trim().toUpperCase() === "DELETE";
  const canSubmit = !!reason && deleteReady && !busy;

  const submit = () => {
    if (!canSubmit) return;
    const meta = REASONS.find((r) => r.key === reason);
    onSubmit({ reason, note: note.trim(), celebrate: !!meta?.celebration });
  };

  return (
    <div
      data-testid={`account-action-modal-${mode}`}
      className="fixed inset-0 z-[120] flex items-start justify-center pt-12 md:pt-16 px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className={`panel-glass corner-marks relative w-full max-w-xl p-6 md:p-8 ${accentBorder}`}>
        <button
          data-testid={`account-action-close-${mode}`}
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
        >
          <X className="w-4 h-4" />
        </button>

        <div className={`terminal-label mb-3 ${accent}`}>
          {isDelete ? "[ CONFIRM.DELETE ]" : "[ PAUSE.ACCOUNT ]"}
        </div>
        <div className="flex items-start gap-3 mb-4">
          <Icon className={`w-6 h-6 flex-none ${accent}`} strokeWidth={1.5} />
          <div>
            <h3 className="serif-mod text-2xl tracking-tight mb-2" style={{ fontWeight: 500 }}>
              {isDelete ? "This is permanent." : "Pause your account."}
            </h3>
            <p className="text-sm text-foreground/75 leading-relaxed">
              {isDelete
                ? "Your profile, generated materials, and application history will be erased. This can't be undone."
                : "We'll freeze your feed, AI generation, and sending. Your profile, sent applications, and public portfolio URLs stay live so you can come back anytime."}
            </p>
          </div>
        </div>

        <div className="mb-5">
          <div className="terminal-label mb-3">Help us understand · what's the main reason?</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {REASONS.map((r) => {
              const selected = reason === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`reason-${r.key}`}
                  onClick={() => setReason(r.key)}
                  disabled={busy}
                  className={`text-left px-3 py-2.5 text-xs font-mono uppercase tracking-[0.12em] border transition-colors disabled:opacity-50 ${
                    selected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-foreground/25 text-foreground/80 hover:border-foreground/55 hover:text-foreground"
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-5">
          <label className="terminal-label block mb-2" htmlFor="account-action-note">
            Anything else? <span className="text-muted-foreground/70 normal-case tracking-normal text-[10px]">(optional)</span>
          </label>
          <textarea
            id="account-action-note"
            data-testid="account-action-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
            rows={3}
            maxLength={1000}
            placeholder={reason === "got_a_job" ? "Where are you heading? We'd love to hear about it." : "Feedback, blockers, anything that would have changed your mind…"}
            className="input-base w-full resize-y text-xs leading-relaxed"
          />
        </div>

        {isDelete && (
          <div className="mb-5">
            <p className="text-xs text-muted-foreground mb-2">
              Type <span className="font-mono text-destructive">DELETE</span> to confirm permanent removal:
            </p>
            <input
              data-testid="delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={busy}
              placeholder="DELETE"
              className="input-base w-full"
            />
          </div>
        )}

        {isDelete && (
          <div className="mb-5 flex items-start gap-2 p-3 border border-destructive/30 bg-destructive/5 text-xs text-destructive/90 font-mono leading-relaxed">
            <AlertTriangle className="w-3.5 h-3.5 flex-none mt-0.5" strokeWidth={1.75} />
            <span>If you just need a break, try <strong>Pause</strong> instead - your data stays put.</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            onClick={onClose}
            disabled={busy}
            className="btn-ghost text-[11px]"
            data-testid={`account-action-cancel-${mode}`}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className={`text-[10px] uppercase tracking-[0.2em] font-mono px-4 py-2.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              isDelete
                ? "border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "border-accent-amber bg-accent-amber text-background hover:bg-accent-amber/90"
            }`}
            data-testid={`account-action-submit-${mode}`}
          >
            {busy ? "…" : isDelete ? "Permanently delete →" : "Pause account →"}
          </button>
        </div>
      </div>
    </div>
  );
}
